import axios from 'axios';
import { setupRetryInterceptor } from './networkRetry';
import StorageManager from './StorageManager';
import { notifySectionPaused } from '../componants/ValveSystem/valveEvents';

// Determine base URL based on current origin to support both apex and www
const getBaseUrl = () => {
  try {
    return window.location.origin; // e.g., https://www.iburrow.com
  } catch (e) {
    return 'http://localhost:2401';
  }
};

// Create axios instance with proper configuration
const apiClient = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL || getBaseUrl(), // Backend/API host (no trailing /api)
  timeout: 30000, // Increased from 10000ms to 30000ms (30 seconds)
  headers: {
    'Content-Type': 'application/json',
  }
});

// Log the API connection configuration (helpful for debugging)
console.log(`[apiConfig] Connecting to API at: ${apiClient.defaults.baseURL} (Source: ${process.env.REACT_APP_API_BASE_URL ? 'REACT_APP_API_BASE_URL env var' : 'default fallback'})`);

// Add request interceptor to include auth token in every request
apiClient.interceptors.request.use(
  config => {
    const token = StorageManager.get('burrowtoken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      console.log('API request with auth token:', config.url);
    } else {
      console.warn('API request without auth token:', config.url);
    }
    return config;
  },
  error => {
    console.error('API request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor — auto-clear auth on 401 (expired/invalid token)
apiClient.interceptors.response.use(
  response => response,
  error => {
    // Valve system: a paused section 503s non-exempt users → lift to the React
    // layer so the app can send the member to the /status door (never a raw 503).
    if (error.response?.status === 503 && error.response?.data?.sectionPaused === true) {
      try {
        notifySectionPaused(error.response.data);
      } catch (e) { /* non-fatal */ }
    }
    if (error.response?.status === 401) {
      const token = StorageManager.get('burrowtoken');
      if (token) {
        // Token exists but server rejected it — session expired
        console.warn('[apiConfig] 401 received with token present — clearing stale auth');
        StorageManager.remove('burrowtoken');
        StorageManager.remove('burrowUserData');
        StorageManager.remove('isAdmin');
        StorageManager.remove('adminLevel');
        // Dispatch a custom event so App.js can trigger Redux logout
        window.dispatchEvent(new CustomEvent('burrow-session-expired'));
      }
    }
    return Promise.reject(error);
  }
);

// Setup automatic retry with exponential backoff (must be added after other interceptors)
setupRetryInterceptor(apiClient, {
  maxRetries: 3,
  retryDelay: 1000,
  maxDelay: 5000
});

// Use a real endpoint that definitely exists for health check
apiClient.checkConnection = async () => {
  try {
    // Try to access any existing endpoint that's lightweight
    await apiClient.get('/');
    return true;
  } catch (error) {
    console.error('API connection check:', error.message);
    return false;
  }
};

export default apiClient;
